import React from 'react';

import { useAuth } from '@opendoor/auth-fe';
import { Box, getCSSVariable, Text } from '@opendoor/bricks-next';
import { motion } from 'framer-motion';

import { getInitials } from 'components/landing-pages-v2/utils/getInitials';

import { useT } from 'helpers/i18n';
import { useObservability } from 'helpers/observability';
import type { Lead } from 'helpers/useLeads';

import type { MegaNavItem, MenuLink } from '../../types';
import { MenuLinkItem } from '../components/MenuLinkItem';
import { MobileMegaNavButton } from './MobileMegaNavButton';
import { MobileMenuSectionHeader } from './MobileMenuSectionHeader';

interface MobileMenuListProps {
  megaNavData?: MegaNavItem[];
  menuPanelNavLinks: MenuLink[];
  mainNavLinks: MenuLink[];
  slug: string;
  onSubMenuOpen: (itemName: string) => void;
  isLoggedIn?: boolean;
  leads: Lead[];
  hasActiveOffers: boolean;
  hasResaleDashboard: boolean;
  footer?: React.ReactNode;
}

const mainMenuVariants = {
  hidden: {
    x: '-100%',
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
  visible: {
    x: 0,
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
};

export const MobileMenuList: React.FC<MobileMenuListProps> = ({
  megaNavData,
  menuPanelNavLinks,
  mainNavLinks,
  slug,
  onSubMenuOpen,
  isLoggedIn,
  leads,
  hasActiveOffers,
  hasResaleDashboard,
  footer,
}) => {
  const { user: human } = useAuth();
  const { trackEvent } = useObservability();
  const { t } = useT();
  const hasLeads = leads && leads.length > 0;

  const fullName = human?.fullName;
  const initials = getInitials(fullName, human?.firstName);

  const renderAvatar = () => {
    if (!fullName) return null;
    return (
      <Box
        backgroundColor="$backgroundSecondary"
        borderTopWidth={1}
        borderColor="$borderPrimary"
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap="$8x"
        paddingTop="$8x"
        paddingBottom="$8x"
        paddingLeft="$12x"
        paddingRight="$12x"
      >
        <Box
          width={32}
          height={32}
          borderRadius={9999}
          backgroundColor="$backgroundInversePrimary"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text
            tag="span"
            typography="$labelSmall"
            fontWeight="$medium"
            color="$contentInversePrimary"
          >
            {initials.join('')}
          </Text>
        </Box>
        <Text tag="span" typography="$labelMedium">
          {fullName}
        </Text>
      </Box>
    );
  };

  const renderAccountItems = () => {
    if (!isLoggedIn) return null;
    return (
      <>
        {hasLeads &&
          leads
            .slice(0, 3)
            .map((lead) => (
              <MenuLinkItem
                key={lead.id}
                href={lead.url}
                name={`${lead.address.street || ''}${
                  lead.readable_offer_state ? ` (${lead.readable_offer_state})` : ''
                }`}
                analyticsName=""
                isActive={false}
              />
            ))}
        {leads && leads.length > 3 && (
          <MenuLinkItem
            key="more-leads"
            href="/properties"
            name="More"
            analyticsName=""
            isActive={slug === '/properties'}
          />
        )}
        {hasResaleDashboard && (
          <MenuLinkItem
            key="closing-dash"
            href="/closing-dashboard"
            name="Closing dash"
            analyticsName=""
            isActive={slug === '/closing-dashboard'}
          />
        )}
        {hasActiveOffers && (
          <MenuLinkItem
            key="dashboard"
            href="/dashboard"
            name="Dashboard"
            analyticsName=""
            isActive={slug === '/dashboard'}
            onPress={() =>
              trackEvent('cta-click', 'dashboard-link', undefined, {
                categoryOverride: 'homepage',
              })
            }
          />
        )}
        <MenuLinkItem
          key="account"
          href="/account"
          name="Account"
          analyticsName=""
          isActive={slug === '/account'}
          isLast
          onPress={() =>
            trackEvent('cta-click', 'account-link', undefined, { categoryOverride: 'homepage' })
          }
        />
      </>
    );
  };

  const hasPanelLinks = menuPanelNavLinks && menuPanelNavLinks.length > 0;

  const navItems = megaNavData
    ? megaNavData.map((item, idx) => {
        const hasSections = item.sections && item.sections.length > 0;
        const isLastNav = idx === megaNavData.length - 1 && !hasPanelLinks;

        if (hasSections) {
          return (
            <MobileMegaNavButton
              key={`mega-${idx}`}
              itemName={item.name}
              onClick={() => onSubMenuOpen(item.name)}
              isLast={isLastNav}
            />
          );
        }

        return (
          <MenuLinkItem
            key={`mega-${idx}`}
            href={item.href}
            name={item.name}
            analyticsName={`cosmos-mega-nav-mobile-${item.name.toLowerCase()}`}
            isActive={slug === item.href}
            isLast={isLastNav}
          />
        );
      })
    : mainNavLinks.map((nav, idx) => (
        <MenuLinkItem
          key={`main-${idx}`}
          href={nav.href}
          name={nav.name}
          analyticsName={`cosmos-landing-page-v2-nav-link-${nav.name}`}
          isActive={slug === nav.href}
          isLast={idx === mainNavLinks.length - 1 && !hasPanelLinks}
        />
      ));

  const content = (
    <>
      {renderAvatar()}
      {isLoggedIn && (
        <Box borderTopWidth={1} borderColor="$borderPrimary">
          {renderAccountItems()}
        </Box>
      )}
      <MobileMenuSectionHeader label={t('homepage.mobile_menu.resources', 'Resources')} />
      <Box>
        {navItems}
        {menuPanelNavLinks?.map((link, idx) => (
          <MenuLinkItem
            key={`panel-${idx}`}
            href={link.href}
            name={link.name}
            analyticsName={`cosmos-landing-page-v2-menu-link-${link.name}`}
            isActive={slug === link.href}
            isLast={idx === menuPanelNavLinks.length - 1}
          />
        ))}
      </Box>
    </>
  );

  if (megaNavData) {
    return (
      <motion.div
        initial="hidden"
        animate="visible"
        exit="hidden"
        variants={mainMenuVariants}
        style={{
          overflowY: 'auto',
          overflowX: 'hidden',
          background: getCSSVariable('$backgroundPrimary', 'color'),
        }}
      >
        {content}
        {footer}
      </motion.div>
    );
  }

  return (
    <>
      {content}
      {footer}
    </>
  );
};
